import React from 'react';

import { useSelector } from 'react-redux';

import dynamic from 'next/dynamic';

import { useFeatures } from '@atc/features';

import {
    Box,
    BoxGrid,
} from 'reaxl';

import inventoryDuck from '@/ducks/inventoryDuck.js';
import userDuck from '@/ducks/userDuck.js';

import usePreQualEligibility from '@/hooks/usePreQualEligibility';

import LazyComponent from '@/components/LazyComponent';
import SponsoredInsuranceContainer from '@/components/vehicledetails/SponsoredInsuranceContainer';

// TODO: Remove after Root sponsorship is done, this is only available for users/dealers in these states
const eligibleStates = ['AL', 'AZ', 'AR', 'CO', 'DE', 'GA', 'IN', 'IA', 'IL', 'KS', 'KY', 'LA', 'MD', 'MN', 'MS', 'MO', 'NE', 'NM', 'OH', 'OK', 'OR', 'PA', 'SC', 'TN', 'TX', 'UT', 'VA', 'WV', 'WI'];

const getIsStateEligible = (userState, dealerState) => {
    if (userState) {
        return eligibleStates?.includes(userState);
    }

    return eligibleStates?.includes(dealerState);
};

const PreQualCTAContainer = dynamic(() => import('@/containers/vdp/PreQualCTAContainer'), { ssr: false });

export function LenderPreQualWrapper({
    listing,
    zip,
}) {

    const logoMap = {
        root: 'root_ins_logo.svg',
    };

    const {
        nds_sponsored_insurance: [enableNdsSponsoredInsurance, {
            external_host: externalHost,
            zip_param: zipParam,
            vin_param: vinParam,
            sponsor_name: sponsorName,
        } = {}] = [],
    } = useFeatures([
        'nds_sponsored_insurance',
    ]);

    const lenderInsuranceItems = [];

    const {
        isPreQualEligible = false,
    } = usePreQualEligibility(listing);

    const userLocation = useSelector(userDuck.selectors.getLocation);
    const inventory = useSelector(inventoryDuck.selectors.selectHydratedInventory);
    const owner = inventory[listing?.id]?.owner;
    const ownerLocation = owner?.location?.address;

    const isStateEligible = getIsStateEligible(userLocation?.state, ownerLocation?.state);
    const showInsurancePartner = enableNdsSponsoredInsurance && listing?.insurancePartnerEnabled && isStateEligible;

    if (zip && showInsurancePartner) {
        lenderInsuranceItems.push(
            <Box
                key="sponsored-insurance-box"
                backgroundColor="neutral-secondary"
                className="rounded"
                border
            >
                <SponsoredInsuranceContainer
                    externalHost={externalHost}
                    key="sponsoredInsuranceContainer"
                    logoMap={logoMap}
                    sponsorName={sponsorName}
                    vinParam={vinParam}
                    zip={zip}
                    zipParam={zipParam}
                    {...listing}
                />
            </Box>
        );
    }

    if (isPreQualEligible) {
        lenderInsuranceItems.push(
            <Box
                key="prequal_cta_wrapper_box"
                backgroundColor="neutral-secondary"
                className="rounded"
                border
            >
                <LazyComponent
                    key="prequal_cta_wrapper"
                    fallback={null}
                >
                    <PreQualCTAContainer uiContext={lenderInsuranceItems.length > 0 ? 'section' : 'styled'} />
                </LazyComponent>
            </Box>
        );
    }

    if (!lenderInsuranceItems.length) {
        return null;
    }

    return (
        <BoxGrid
            key="lender-insurance-wrapper-grid"
            data-cmp="lender-insurance-wrapper"
            backgroundcolor="neutral-secondary"
            className="box-grid box-grid-col-sm-2"
        >
            {lenderInsuranceItems}
        </BoxGrid>
    );
}

export default LenderPreQualWrapper;
